import { useState } from "react";
import { MENU_ROOT_STATE } from "@/types/NavigationState";
import { useHistory } from "react-router-dom";
import { Collapse, Nav } from "react-bootstrap";
import type { MenuRoute } from "../Menus/menuTypes";
import SntLabIcon from "@icons/SntLabIcon";
import SensolusOnlyBadge from "@/cleanup/widgets/SensolusOnlyBadge";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@hooks/useLanguage";

/**
 * Location object from react-router-dom
 */
interface Location {
  pathname: string;
  search?: string;
  hash?: string;
  state?: unknown;
}

/**
 * Extended MenuRoute interface with additional properties used in NavList
 */
interface ExtendedMenuRoute extends MenuRoute {
  path?: string;
  redirect?: boolean;
  title?: string;
  param?: string;
  category?: string;
  reportType?: string;
  type?: string;
  background?: string;
  excludeSubPages?: string[];
  views?: ExtendedMenuRoute[];
  isLabFeature?: boolean;
}

/**
 * Props for the NavList component
 */
interface NavListProps {
  collapsible?: boolean;
  routes?: ExtendedMenuRoute[];
  location?: Location;
  orgId?: string;
  menuType?: string;
}

/**
 * State for managing collapse states of menu items
 */
type CollapseState = Record<string, boolean>;

/**
 * Verifies if routeName is the one active (in browser input)
 */
export const activeRoute = (prop: ExtendedMenuRoute, location: Location): string => {
  let routeName = "";
  let to = prop.to;

  if (prop.path) {
    to = prop.path;
  }
  routeName = (prop.layout || "") + to;

  const subPages = prop.subPages || [];
  const excludeSubPages = prop.excludeSubPages || [];

  // Check if current path should be excluded (e.g., site maps shown in home menu)
  const isExcluded = excludeSubPages.some(
    (excludePath) => location.pathname === excludePath
  );

  // Check exact match for main route, or if pathname matches subPage (exact or with path params)
  const isSubPageActive =
    !isExcluded &&
    subPages.some(
      (subPage) =>
        location.pathname === subPage ||
        location.pathname.startsWith(subPage + "/")
    );
  return location.pathname === routeName || isSubPageActive ? "active" : "";
};

export const expanded = (prop: ExtendedMenuRoute, location: Location): boolean => {
  return prop.views?.find((r) => r.to === location.pathname) ? true : false;
};

export const expandedCheckSubTab = (prop: ExtendedMenuRoute, location: Location): boolean => {
  return prop?.views?.find((r) =>
    r?.subPages?.find(
      (x) =>
        x === location.pathname || location.pathname.startsWith(x + "/")
    )
  )
    ? true
    : false;
};

function NavList({
  collapsible = true,
  routes = [],
  location = { pathname: "" } as Location,
  orgId,
  menuType,
}: NavListProps) {
  const history = useHistory();
  const reportOrgId = localStorage.getItem("reportOrgId");

  // Handle navigation with deferred execution to prevent DOM race conditions
  const handleNavigation = (e: React.MouseEvent, targetPath: string) => {
    e.preventDefault();
    // Use requestAnimationFrame to defer navigation until after current DOM operations complete
    requestAnimationFrame(() => {
      // menuRoot: a sidebar-menu click starts a fresh navigation root —
      // AppLayout resets the back-arrow history depth so top-level pages
      // never show the arrow, only drill-ins from here do (STIC-15588)
      history.push(targetPath, MENU_ROOT_STATE);
    });
  };

  // this verifies if any of the collapses should be default opened on a rerender of this component
  // for example, on the refresh of the page,
  // while on the src/views/forms/RegularForms.jsx - route /regular-forms
  const getCollapseInitialState = (routes: ExtendedMenuRoute[]): boolean => {
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse && routes[i].views && getCollapseInitialState(routes[i].views || [])) {
        return true;
      } else if (location && location.pathname === (routes[i].layout || "") + routes[i].to) {
        return true;
      } else if (
        routes[i].path &&
        location &&
        location.pathname === (routes[i].layout || "") + routes[i].path
      ) {
        return true;
      }
    }
    return false;
  };

  // this creates the intial state of this component based on the collapse routes
  // that it gets through routes prop
  const getCollapseStates = (routes: ExtendedMenuRoute[]): CollapseState => {
    let initialState: CollapseState = {};
    routes.map((prop) => {
      if (prop.collapse && prop.state) {
        initialState = {
          [prop.state]: getCollapseInitialState(prop.views || []),
          ...getCollapseStates(prop.views || []),
          ...initialState,
        };
      }
      return null;
    });
    return initialState;
  };

  // this is for the rest of the collapses
  const [state, setState] = useState<CollapseState>(getCollapseStates(routes));

  // this function creates the links and collapses that appear in the sidebar (left menu)
  const createLinksDashboard = (routes: ExtendedMenuRoute[]) => {
    const organisationDashboards: ExtendedMenuRoute[] = [];
    const workspaceDashboards: ExtendedMenuRoute[] = [];
    const normalDashboards: ExtendedMenuRoute[] = [];
    const otherDashboards: ExtendedMenuRoute[] = [];
    const topMenu: ExtendedMenuRoute[] = [];

    for (let index = 0; index < routes.length; index++) {
      const element = routes[index];
      if (element.reportType === "ORGANIZATION") {
        organisationDashboards.push(element);
      } else if (element.reportType === "WORKSPACE") {
        workspaceDashboards.push(element);
      } else if (element.reportType === "NORMAL") {
        normalDashboards.push(element);
      } else if (element.type === "bi") {
        otherDashboards.push(element);
      } else if (element.view) {
        topMenu.push(element);
      }
    }
    return (
      <>
        {createLinks(topMenu)}
        {topMenu.length > 0 &&
          (organisationDashboards.length > 0 ||
            normalDashboards.length > 0 ||
            workspaceDashboards.length > 0 ||
            otherDashboards.length > 0) && (
            <hr
              style={{
                color: "lime",
                height: "1px",
                margin: "10px 10px 10px 40px",
              }}
            />
          )}
        {createLinks(organisationDashboards)}
        {organisationDashboards.length > 0 &&
          (normalDashboards.length > 0 ||
            workspaceDashboards.length > 0 ||
            otherDashboards.length > 0) && (
            <hr
              style={{
                color: "lime",
                height: "1px",
                margin: "10px 10px 10px 40px",
              }}
            />
          )}
        {createLinks(normalDashboards)}
        {normalDashboards.length > 0 &&
          (workspaceDashboards.length > 0 || otherDashboards.length > 0) && (
            <hr
              style={{
                color: "lime",
                height: "1px",
                margin: "10px 10px 10px 40px",
              }}
            />
          )}
        {createLinks(workspaceDashboards)}
        {workspaceDashboards.length > 0 && otherDashboards.length > 0 && (
          <hr
            style={{
              color: "lime",
              height: "1px",
              margin: "10px 10px 10px 40px",
            }}
          />
        )}
        {createLinks(otherDashboards)}
      </>
    );
  };

  const createLinks = (routes: ExtendedMenuRoute[]) => {
    return routes.map((prop, key) => {
      if (prop.redirect) {
        return null;
      }
      const isLastTopOverview =
        prop.topOverview === true &&
        (key === routes.length - 1 || routes[key + 1]?.topOverview !== true);
      if (prop.collapse) {
        const st: CollapseState = {};
        if (prop.state) {
          st[prop.state] = !state[prop.state];
        }
        return (
          (prop.view === undefined || prop.view === null || prop.view === true
            ? prop.views && prop.views.length > 0
              ? prop.views.find((item) => item.view)
              : true
            : false) && ( // If a value for view is false, it overrides the check for at least one visible view in the views property
            <Nav.Item
              className={getCollapseInitialState(prop.views || []) ? "active" : ""}
              as="li"
              key={key}
              style={{ background: prop.background }}
            >
              <Nav.Link
                className={
                  (prop.state && state[prop.state]) ||
                  !collapsible ||
                  (location && expanded(prop, location)) ||
                  (location && expandedCheckSubTab(prop, location))
                    ? "collapsed nav-category"
                    : "nav-category"
                }
                data-toggle="collapse"
                onClick={(e) => {
                  e.preventDefault();
                  setState({ ...state, ...st });
                }}
                aria-expanded={
                  (prop.state && state[prop.state]) ||
                  (location && expanded(prop, location)) ||
                  (location && expandedCheckSubTab(prop, location))
                }
                style={{ cursor: "default" }}
              >
                {prop.icon}
                <p
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={prop.title || (typeof prop.name === "string" ? prop.name : undefined)}
                >
                  {prop.name}
                  <SensolusOnlyBadge systemOnly={prop.systemOnly} />
                  {collapsible && <b className="caret"></b>}
                </p>
              </Nav.Link>
              <Collapse
                in={
                  (prop.state && state[prop.state]) ||
                  !collapsible ||
                  (location && expanded(prop, location)) ||
                  (location && expandedCheckSubTab(prop, location))
                }
              >
                <div>
                  <Nav as="ul">
                    {prop.name === "Dashboards"
                      ? createLinksDashboard(prop.views || [])
                      : createLinks(prop.views || [])}
                  </Nav>
                </div>
              </Collapse>
            </Nav.Item>
          )
        );
      }
      return (
        prop.view && (
          <Nav.Item
            className={[
              location ? activeRoute(prop, location) : "",
              prop.topOverview ? "top-overview" : "",
              isLastTopOverview ? "top-overview-last" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            key={key}
            as="li"
          >
            <Nav.Link
              href={
                (prop.layout || "") +
                `${
                  orgId && menuType !== "MAIN"
                    ? `${prop.to}?org=${orgId}${
                        prop.param ? `&` + prop.param : ""
                      }`
                    : `${
                        prop.category === "dashboard" && reportOrgId
                          ? prop.to + "?org=" + reportOrgId
                          : prop.to
                      }`
                }`
              }
              onClick={(e) =>
                handleNavigation(
                  e,
                  (prop.layout || "") +
                    `${
                      orgId && menuType !== "MAIN"
                        ? `${prop.to}?org=${orgId}${
                            prop.param ? `&` + prop.param : ""
                          }`
                        : `${
                            prop.category === "dashboard" && reportOrgId
                              ? prop.to + "?org=" + reportOrgId
                              : prop.to
                          }`
                    }`
                )
              }
              // Rule #5 (STIC-15534/STIC-15615 R9+R11): level-unavailable
              // items (the hook decided they don't apply at the selected
              // level) stay visible but greyed — never hidden, still fully
              // clickable/navigable
              className={prop.disabled ? "opacity-50" : undefined}
              style={{
                cursor: "pointer",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={prop.title || (typeof prop.name === "string" ? prop.name : undefined)}
            >
              {prop.topOverview && (
                <span className="top-overview-icon-wrap">{prop.icon}</span>
              )}
              {prop.name && (
                <>
                  <span className="sidebar-normal">
                    {prop.name}
                    <SensolusOnlyBadge systemOnly={prop.systemOnly} />
                    {prop.isLabFeature && (
                      <SntLabIcon
                        className="ms-2"
                        size={14}
                        style={{ marginTop: "-2px" }}
                        title={translate(LanguageKey.lab_feature_key)}
                      />
                    )}
                  </span>
                </>
              )}
            </Nav.Link>
          </Nav.Item>
        )
      );
    });
  };

  return <Nav as="ul">{createLinks(routes)}</Nav>;
}

export default NavList;
